import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieContext = createContext();

const COOKIE_KEY = 'easyweb-cookie-consent';

export function CookieProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  const initializeAnalytics = () => {
    // Google Analytics initialization
    // This will be triggered only after consent
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_KEY);
    if (savedConsent) {
      const parsedConsent = JSON.parse(savedConsent);
      setConsent(parsedConsent);
      setShowBanner(false);
      
      // Re-initialize analytics if previously consented
      if (parsedConsent.analytics) {
        initializeAnalytics();
      }
    } else {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const newConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    setConsent(newConsent);
    localStorage.setItem(COOKIE_KEY, JSON.stringify(newConsent));
    setShowBanner(false);
    
    // Initialize analytics if accepted
    if (newConsent.analytics) {
      initializeAnalytics();
    }
  };

  const acceptNecessary = () => {
    const newConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    setConsent(newConsent);
    localStorage.setItem(COOKIE_KEY, JSON.stringify(newConsent));
    setShowBanner(false);
  };

  const updateConsent = (settings) => {
    const newConsent = {
      ...settings,
      necessary: true, // Always required
      timestamp: new Date().toISOString()
    };
    setConsent(newConsent);
    localStorage.setItem(COOKIE_KEY, JSON.stringify(newConsent));
    setShowBanner(false);
    
    if (newConsent.analytics) {
      initializeAnalytics();
    }
  };

  const openSettings = () => {
    setShowBanner(true);
  };

  return (
    <CookieContext.Provider value={{
      consent,
      showBanner,
      setShowBanner,
      acceptAll,
      acceptNecessary,
      updateConsent,
      openSettings,
      hasAnalyticsConsent: consent?.analytics || false,
      hasMarketingConsent: consent?.marketing || false
    }}>
      {children}
    </CookieContext.Provider>
  );
}

export function useCookies() {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies must be used within a CookieProvider');
  }
  return context;
}
